import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { RaptorLogo } from '@/components/RaptorLogo';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { toast } from 'sonner';
import { Smartphone, Mail, KeyRound, CheckCircle2, Loader2 } from 'lucide-react';

type Step = 'details' | 'sms' | 'email' | 'password' | 'done';

const errMsg = (e: unknown, fb: string) =>
  (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fb;

export const RegisterPage: React.FC = () => {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [step, setStep] = useState<Step>('details');
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [smsChallenge, setSmsChallenge] = useState('');
  const [emailChallenge, setEmailChallenge] = useState('');
  const [smsMasked, setSmsMasked] = useState('');
  const [emailMasked, setEmailMasked] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    if (!token) { setInvalid(true); setLoading(false); return; }
    void (async () => {
      try {
        const r = await api.get(`/public/activate/resolve/${token}`);
        const parts = (r.data.full_name || '').trim().split(' ');
        setName(parts[0] || '');
        setSurname(parts.slice(1).join(' '));
        setEmail(r.data.email || '');
      } catch { setInvalid(true); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const sendDetails = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim() || !surname.trim() || !mobile.trim()) { toast.error('Please complete all fields.'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/public/activate/${token}/start`, { full_name: `${name.trim()} ${surname.trim()}`, phone: mobile.trim() });
      setSmsChallenge(r.data.challenge_id); setSmsMasked(r.data.identifier_masked); setCode('');
      setStep('sms');
      toast.success('We sent a code to your mobile.');
    } catch (err) { toast.error(errMsg(err, 'Could not send the code.')); }
    finally { setBusy(false); }
  };

  const verifySms = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/public/activate/${token}/sms-verify`, { challenge_id: smsChallenge, code: code.trim() });
      setEmailChallenge(r.data.email_challenge_id); setEmailMasked(r.data.email_masked); setCode('');
      setStep('email');
      toast.success('Mobile verified. We sent a code to your email.');
    } catch (err) { toast.error(errMsg(err, 'Invalid code.')); }
    finally { setBusy(false); }
  };

  const verifyEmail = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/public/activate/${token}/email-verify`, { challenge_id: emailChallenge, code: code.trim() });
      setCode(''); setStep('password');
      toast.success('Email verified. Set your password.');
    } catch (err) { toast.error(errMsg(err, 'Invalid code.')); }
    finally { setBusy(false); }
  };

  const complete = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (password !== confirm) { toast.error('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await api.post(`/public/activate/${token}/complete`, { password, confirm });
      setStep('done');
    } catch (err) { toast.error(errMsg(err, 'Could not complete registration.')); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-raptor-neutral flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="register-card">
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center"><RaptorLogo size={56} slot="login_logo" /></div>
          <CardTitle>Complete your registration</CardTitle>
          <CardDescription>
            {step === 'details' && !invalid && 'Confirm your details to get started.'}
            {invalid && 'This registration link can’t be used.'}
            {step === 'sms' && `Enter the code sent to ${smsMasked}.`}
            {step === 'email' && `Enter the code sent to ${emailMasked}.`}
            {step === 'password' && 'Choose a password for your account.'}
            {step === 'done' && 'You are all set.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 grid place-items-center text-raptor-navy/50"><Loader2 className="animate-spin" /></div>
          ) : invalid ? (
            <div className="py-6 text-center space-y-3" data-testid="register-invalid">
              <p className="text-sm text-raptor-navy/70">This registration link is invalid or has expired. Please contact your administrator.</p>
              <Button variant="outline" onClick={() => nav('/login')}>Go to login</Button>
            </div>
          ) : step === 'details' ? (
            <form onSubmit={sendDetails} className="space-y-3" data-testid="register-details-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-name" /></div>
                <div className="space-y-1.5"><Label>Surname</Label><Input value={surname} onChange={(e) => setSurname(e.target.value)} data-testid="reg-surname" /></div>
              </div>
              <div className="space-y-1.5"><Label>Email address</Label><Input value={email} readOnly disabled data-testid="reg-email" /></div>
              <div className="space-y-1.5"><Label>Mobile number</Label><Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="082 123 4567" data-testid="reg-mobile" /></div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="reg-send-sms">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <><Smartphone size={16} /> Send SMS code</>}
              </Button>
            </form>
          ) : step === 'sms' ? (
            <form onSubmit={verifySms} className="space-y-3" data-testid="register-sms-form">
              <div className="space-y-1.5"><Label>SMS code</Label><Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} data-testid="reg-sms-code" /></div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="reg-verify-sms">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <><KeyRound size={16} /> Verify mobile</>}
              </Button>
            </form>
          ) : step === 'email' ? (
            <form onSubmit={verifyEmail} className="space-y-3" data-testid="register-email-form">
              <div className="space-y-1.5"><Label>Email code</Label><Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} data-testid="reg-email-code" /></div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="reg-verify-email">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <><Mail size={16} /> Verify email</>}
              </Button>
            </form>
          ) : step === 'password' ? (
            <form onSubmit={complete} className="space-y-3" data-testid="register-password-form">
              <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reg-password" /></div>
              <div className="space-y-1.5"><Label>Re-enter password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reg-confirm" /></div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="reg-complete">
                {busy ? <Loader2 size={16} className="animate-spin" /> : 'Create my account'}
              </Button>
            </form>
          ) : (
            <div className="py-6 text-center space-y-4" data-testid="register-done">
              <CheckCircle2 size={48} className="mx-auto text-emerald-500" />
              <div>
                <p className="font-semibold text-raptor-navy">Registration successful!</p>
                <p className="text-sm text-raptor-navy/60 mt-1">Your account is ready. You can now sign in.</p>
              </div>
              <Button className="w-full" onClick={() => nav('/login')} data-testid="reg-goto-login">Close and go to login</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
